import * as React from 'react';
import _ from 'lodash';
import {SyntheticEvent, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {sha256} from 'js-sha256';
import {Box, Tabs, IconButton} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/SearchOff';
import {useQuery} from '@tanstack/react-query';
import StyledTab from '../../shared/Tabs/StyledTab';
import TabPanel from '../../shared/Tabs/TabPanel';
import Loader from '../../shared/Loader';
import CustomSelect from '../../shared/CustomSelect';
import CustomMultipleSelect from '../../shared/CustomMultipleSelect';
import apiCall, {setUserToken} from '../../utils/api';
import {greater} from '@/utils/dates';
import Output from './Output';
import ShortTerm from './ShortTerm';
import MidTerm from './MidTerm';
import AcceleratorMidTerm from './AcceleratorMidTerm';
import LongTerm from './LongTerm';
import Impact from './Impact';
import {AcceleratorLongTerm} from './AcceleratorLongTerm';
import {AcceleratorImpact} from './AcceleratorImpact';
import {IFieldProps} from '@/resources/Field';
import {ContainerProvider} from '@/contexts/Container';
import {getValueByType, TYPE_ACCELERATOR, TYPE_MBA_STUDENT} from './helpers/mapping';

import './styles.scss';

function tabProps(index: number) {
    return {
        id: `tab-${index}`,
        'aria-controls': `tabpanel-${index}`,
    };
}

interface AppliedFilters {
    levelIds: any[] | null;
    selectedGender: string;
    selectedHighPotential: string;
    selectedUoas: string[];
    selectedImpactSampleSector: string;
}

const DEFAULT_FILTERS: AppliedFilters = {
    levelIds: null,
    selectedGender: 'All',
    selectedHighPotential: 'All',
    selectedUoas: [],
    selectedImpactSampleSector: 'All',
};

const E4ImpactMBA = () => {
    const [searchParams] = useSearchParams();
    const container = searchParams.get('container_id');
    const hashKeyLocal = sha256(process.env.REACT_APP_HASH_KEY_E4_IMPACT || '');
    const containerId = searchParams.get('container_id');
    const typeId = searchParams.get('type_id');
    const baselineId = searchParams.get('baseline_id');
    const finalId = searchParams.get('final_id');
    const hashKey = searchParams.get('key');
    const uoaId = searchParams.get('uoa_id');

    setUserToken(searchParams.get('token') || '');

    const isAuthorized = hashKey === hashKeyLocal;
    const businessSectorQuestionId = getValueByType(typeId || '', 'type_of_sector');

    // Pending (UI) filter state — changes on every select interaction
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [selectedEditions, setSelectedEditions] = useState<string[]>([]);
    const [selectedGender, setSelectedGender] = useState<string>('All');
    const [selectedHighPotential, setSelectedHighPotential] = useState<string>('All');
    const [selectedUoas, setSelectedUoas] = useState<string[]>([]);
    const [selectedBusinessSectors, setSelectedBusinessSectors] = useState<string[]>([]);
    const [selectedImpactSampleSector, setSelectedImpactSampleSector] = useState<string>('All');
    const [impactSampleSectorChoices] = useState([
        {id: 1, name: 'Yes'},
        {id: 0, name: 'No'},
    ]);

    // Level/edition derived state (pending)
    const [levelIds, setLevelIds] = useState<any[] | null>(null);
    const [editionsCount, setEditionsCount] = useState(0);
    const [selectableEditions, setSelectableEditions] = useState<any[]>([]);

    // Applied (committed) filter snapshot — only updates when Search is clicked
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>(DEFAULT_FILTERS);

    const [tab, setTab] = useState(0);

    // ─── Query 1: filter metadata ─────────────────────────────────────────────
    const filterQuery = useQuery({
        queryKey: ['e4impact-filter', containerId, baselineId, businessSectorQuestionId],
        queryFn: async () => {
            const [fieldsRes, breakdownsRes, choicesRes] = await Promise.all([
                apiCall({
                    url: `containers/${container}/fields`,
                    params: {container_id: containerId, pagination: 'false'},
                }),
                apiCall({
                    url: `containers/${container}/breakdowns`,
                    params: {container_id: containerId},
                }),
                apiCall({
                    url: `containers/${container}/surveys/questions/choices`,
                    params: {
                        surveyId: baselineId,
                        questionId: businessSectorQuestionId,
                    },
                }),
            ]);
            return {fieldsRes, breakdownsRes, choicesRes};
        },
        enabled: !!(containerId && isAuthorized && baselineId && finalId),
    });

    // Derived: fields & filter UI options
    const fields = useMemo<IFieldProps[]>(() => {
        return filterQuery.data?.fieldsRes?.data?.custom_fields ?? [];
    }, [filterQuery.data]);

    const genderField = useMemo(() => {
        return fields.find((f) => f.key === 'genere');
    }, [fields]);

    const highPotentialField = useMemo(() => {
        return fields.find((f) => f.key === 'alto-potenziale');
    }, [fields]);

    const countries = useMemo(() => {
        const breakdowns = filterQuery.data?.breakdownsRes?.data?.break_downs;
        if (!breakdowns) return [];
        const slug = Number(typeId) === TYPE_ACCELERATOR ? 'business-accelerator-and-incubator' : 'activities';
        const mba = breakdowns.find((b: any) => b.slug === slug);
        return mba ? mba.levels.filter((l: any) => l.parent_id === null) : [];
    }, [filterQuery.data, typeId]);

    const editions = useMemo(() => {
        const breakdowns = filterQuery.data?.breakdownsRes?.data?.break_downs;
        if (!breakdowns) return [];
        const slug = Number(typeId) === TYPE_ACCELERATOR ? 'business-accelerator-and-incubator' : 'activities';
        const mba = breakdowns.find((b: any) => b.slug === slug);
        if (!mba) return [];
        const countryIds = countries.map((c: any) => c.id);
        return mba.levels.filter((l: any) => countryIds.includes(l.parent_id));
    }, [filterQuery.data, typeId, countries]);

    const businessSectorChoices = useMemo(() => {
        const choices = filterQuery.data?.choicesRes?.data?.choices;
        return choices ? choices.map((c: any) => ({id: c.value, name: c.text})) : [];
    }, [filterQuery.data]);

    // Data filter built from committed appliedFilters + loaded field IDs
    const dataFilter = useMemo(() => {
        const uoaFilter = uoaId ? {uoa_id: uoaId} : appliedFilters.selectedUoas.length > 0 ? {uoa_ids: appliedFilters.selectedUoas} : {};

        return {
            ...uoaFilter,
            ...(appliedFilters.levelIds && {
                level_ids: appliedFilters.levelIds,
            }),
            ...(genderField &&
                appliedFilters.selectedGender !== 'All' && {
                    custom_fields: {
                        [genderField.id]: appliedFilters.selectedGender,
                    },
                }),
            ...(highPotentialField &&
                appliedFilters.selectedHighPotential !== 'All' && {
                    custom_fields: {
                        [highPotentialField.id]: appliedFilters.selectedHighPotential,
                    },
                }),
        };
    }, [appliedFilters, genderField, highPotentialField, uoaId]);

    // ─── Query 2: UoA data ────────────────────────────────────────────────────
    const uoaQuery = useQuery({
        queryKey: ['e4impact-uoa', containerId, typeId, dataFilter, appliedFilters.selectedImpactSampleSector, baselineId, finalId],
        queryFn: async () => {
            const res = await apiCall({
                url: `containers/${container}/uoa/search`,
                params: {
                    container_id: containerId,
                    pagination: 'false',
                    type_id: typeId,
                    ...(appliedFilters.selectedImpactSampleSector !== 'All' && {
                        survey_ids: {
                            ids: [baselineId, finalId],
                            exclude: Number(!appliedFilters.selectedImpactSampleSector),
                        },
                    }),
                    ...dataFilter,
                },
            });
            return res.data;
        },
        enabled: filterQuery.isSuccess && !!(containerId && typeId),
    });

    // Derived: UoA stats for Output tab
    const uoaStats = useMemo(() => {
        const items: any[] = uoaQuery.data?.data ?? [];
        const filteredItems = items.filter((item) => appliedFilters.selectedUoas.length === 0 || appliedFilters.selectedUoas.includes(item.id));

        const birthdayField = fields.find((f) => f.key === 'data-di-nascita-attenzione-inserire-la-data-di-nascita-corretta-e-non-una-data-recente');
        const statusField = fields.find((f) => f.key === 'status');

        let women = 0;
        let youngEntrepeneurs = 0;
        let active = 0;
        let notActive = 0;

        if (genderField) {
            const femaleOption = genderField.options.find((o: any) => o.key === 'female');
            women = filteredItems.filter((item) =>
                item.custom_field_values.some((cf: any) => cf.field_id === genderField.id && cf.stored_value === femaleOption?.id.toString()),
            ).length;
        }

        if (birthdayField) {
            youngEntrepeneurs = filteredItems.filter((item) =>
                item.custom_field_values.some((cf: any) => cf.field_id === birthdayField.id && greater(cf.value, '1994-12-31')),
            ).length;
        }

        if (statusField) {
            const activeOpt = statusField.options.find((o: any) => o.key === 'attivo');
            const notActiveOpt = statusField.options.find((o: any) => o.key === 'non-attivo');
            filteredItems.forEach((item) => {
                const cf = item.custom_field_values.find((v: any) => v.field_id === statusField.id);
                if (cf?.stored_value === activeOpt?.id.toString()) active++;
                else if (cf?.stored_value === notActiveOpt?.id.toString()) notActive++;
            });
        }

        return {
            uoas: items.map((item) => ({name: item.title, id: item.id})),
            entrepeneurs: filteredItems.length,
            women,
            youngEntrepeneurs,
            activeEntrepeneurs: {active, not_active: notActive},
        };
    }, [uoaQuery.data, fields, genderField, appliedFilters.selectedUoas]);

    // ─── Query 3+4: baseline & final submissions (parallel) ───────────────────
    const baselineQuery = useQuery({
        queryKey: ['e4impact-baseline', container, baselineId, dataFilter],
        queryFn: async () => {
            const res = await apiCall({
                url: `containers/${container}/submissions`,
                params: {
                    survey_id: baselineId,
                    completed: 1,
                    ...dataFilter,
                    additional_data: false,
                },
            });
            return res.data?.submissions ? _.uniqBy(res.data.submissions, 'uoa_id') : [];
        },
        enabled: filterQuery.isSuccess,
    });

    const finalQuery = useQuery({
        queryKey: ['e4impact-final', container, finalId, dataFilter],
        queryFn: async () => {
            const res = await apiCall({
                url: `containers/${container}/submissions`,
                params: {
                    survey_id: finalId,
                    completed: 1,
                    ...dataFilter,
                    additional_data: false,
                },
            });
            return res.data?.submissions ? _.uniqBy(res.data.submissions, 'uoa_id') : [];
        },
        enabled: filterQuery.isSuccess,
    });

    // Cross-filter baseline/final to only include UoAs present in both
    const {baseline, final} = useMemo(() => {
        const rawBaseline: any[] = baselineQuery.data ?? [];
        const rawFinal: any[] = finalQuery.data ?? [];
        return {
            baseline: rawBaseline.filter(({uoa_id}) => rawFinal.some((f) => f.uoa_id === uoa_id)),
            final: rawFinal.filter(({uoa_id}) => rawBaseline.some((b) => b.uoa_id === uoa_id)),
        };
    }, [baselineQuery.data, finalQuery.data]);

    // Client-side business sector filter (no API re-fetch needed)
    const filteredBaseline = useMemo(() => {
        return baseline.filter((item: any) => selectedBusinessSectors.length === 0 || selectedBusinessSectors.includes(item[businessSectorQuestionId]));
    }, [baseline, selectedBusinessSectors, businessSectorQuestionId]);

    // ─── Edition/level selection logic ────────────────────────────────────────
    useEffect(() => {
        setSelectedEditions([]);
    }, [selectedCountries]);

    useEffect(() => {
        if (!filterQuery.isSuccess) return;
        if (selectedCountries.length > 0) {
            setSelectableEditions(editions.filter((e: any) => selectedCountries.includes(e.parent_id)));
        } else {
            let years = editions.map((e: any) => (Number(typeId) === TYPE_ACCELERATOR ? e.name.substring(3) : e.name.split(' ').at(-1)));
            years = years.filter((y: any, i: number, arr: any[]) => arr.indexOf(y) === i);
            setSelectableEditions(years.map((y: any) => ({id: y, name: y})));
        }
    }, [filterQuery.isSuccess, selectedCountries, editions, typeId]);

    useEffect(() => {
        if (selectedEditions.length > 0) {
            if (selectedCountries.length > 0) {
                setLevelIds([selectedEditions]);
                setEditionsCount(1);
            } else {
                const levels = editions.filter((e: any) => selectedEditions.some((name) => e.name.endsWith(name))).map((e: any) => e.id);
                setLevelIds(levels);
                setEditionsCount(levels.length);
            }
            return;
        }
        if (selectedCountries.length > 0) {
            setLevelIds(selectedCountries);
            setEditionsCount(selectableEditions.length);
            return;
        }
        setEditionsCount(editions.length);
        setLevelIds(null);
    }, [selectedCountries, selectedEditions, editions, selectableEditions]);

    // ─── Search / clear ───────────────────────────────────────────────────────
    const runSearch = () => {
        setAppliedFilters({
            levelIds,
            selectedGender,
            selectedHighPotential,
            selectedUoas,
            selectedImpactSampleSector,
        });
    };

    const clearSearch = () => {
        setSelectedUoas([]);
        setSelectedCountries([]);
        setSelectedEditions([]);
        setLevelIds(null);
        setSelectedGender('All');
        setSelectedHighPotential('All');
        setSelectedBusinessSectors([]);
        setSelectedImpactSampleSector('All');
        setAppliedFilters(DEFAULT_FILTERS);
    };

    const handleChange = (_: SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    const isFetching = filterQuery.isFetching || uoaQuery.isFetching || baselineQuery.isFetching || finalQuery.isFetching;

    const isLoading = !isAuthorized || isFetching;

    return (
        <div className='container space-y-8'>
            {!isLoading && (
                <ContainerProvider type={typeId}>
                    {!uoaId && (
                        <div className='data-filter'>
                            <CustomMultipleSelect
                                items={uoaStats.uoas}
                                value={selectedUoas}
                                onChange={(value: any) => setSelectedUoas(value)}
                                label='UoA'
                                className='uoa-select'
                            />
                            <CustomMultipleSelect
                                items={countries}
                                value={selectedCountries}
                                onChange={(value: any) => setSelectedCountries(value)}
                                label='Country'
                                className='uoa-select'
                            />
                            <CustomMultipleSelect
                                items={selectableEditions}
                                value={selectedEditions}
                                onChange={(value: any) => setSelectedEditions(value)}
                                label='Edition'
                                className='uoa-select'
                            />
                            {genderField && (
                                <CustomSelect
                                    items={genderField.options}
                                    value={selectedGender}
                                    onChange={(value: any) => setSelectedGender(value)}
                                    label={genderField.name}
                                    className='uoa-select'
                                    defaultVariant='All'
                                />
                            )}
                            {highPotentialField && (
                                <CustomSelect
                                    items={highPotentialField.options}
                                    value={selectedHighPotential}
                                    onChange={(value: any) => setSelectedHighPotential(value)}
                                    label={highPotentialField.name}
                                    className='uoa-select'
                                    defaultVariant='All'
                                />
                            )}
                            <CustomMultipleSelect
                                items={businessSectorChoices}
                                value={selectedBusinessSectors}
                                onChange={(value: any) => setSelectedBusinessSectors(value)}
                                label='Business Sector'
                                className='uoa-select'
                            />
                            {Number(typeId) === TYPE_MBA_STUDENT && (
                                <CustomSelect
                                    items={impactSampleSectorChoices}
                                    value={selectedImpactSampleSector}
                                    onChange={(value: any) => setSelectedImpactSampleSector(value)}
                                    label='Impact sample'
                                    className='uoa-select'
                                    defaultVariant='All'
                                />
                            )}
                            <Box
                                sx={{
                                    gridColumn: 'span 1 / -1',
                                    display: 'flex',
                                    width: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'end',
                                }}
                            >
                                <IconButton onClick={runSearch}>
                                    <SearchIcon />
                                </IconButton>
                                <IconButton onClick={clearSearch}>
                                    <CancelIcon />
                                </IconButton>
                            </Box>
                        </div>
                    )}

                    <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                        <Tabs value={tab} onChange={handleChange} aria-label='tabs'>
                            <StyledTab label='OUTPUT' {...tabProps(0)} />
                            <StyledTab label='SHORT-TERM OUTCOMES' {...tabProps(1)} />
                            <StyledTab label='MID-TERM OUTCOMES' {...tabProps(1)} />
                            <StyledTab label='LONG-TERM OUTCOMES' {...tabProps(1)} />
                            <StyledTab label='IMPACT' {...tabProps(1)} />
                        </Tabs>
                    </Box>

                    <TabPanel value={tab} index={0}>
                        <Output
                            baseline={filteredBaseline}
                            entrepeneurs={uoaStats.entrepeneurs}
                            women={uoaStats.women}
                            editions={editionsCount}
                            activeEntrepeneurs={uoaStats.activeEntrepeneurs}
                            youngEntrepeneurs={uoaStats.youngEntrepeneurs}
                            fields={fields}
                        />
                    </TabPanel>

                    <TabPanel value={tab} index={1}>
                        <ShortTerm baseline={filteredBaseline} final={final} />
                    </TabPanel>

                    <TabPanel value={tab} index={2}>
                        {Number(typeId) === TYPE_MBA_STUDENT && <MidTerm baseline={filteredBaseline} final={final} />}
                        {Number(typeId) === TYPE_ACCELERATOR && <AcceleratorMidTerm baseline={filteredBaseline} final={final} />}
                    </TabPanel>

                    <TabPanel value={tab} index={3}>
                        {Number(typeId) === TYPE_MBA_STUDENT && <LongTerm baseline={filteredBaseline} final={final} />}
                        {Number(typeId) === TYPE_ACCELERATOR && <AcceleratorLongTerm baseline={filteredBaseline} final={final} />}
                    </TabPanel>

                    <TabPanel value={tab} index={4}>
                        {Number(typeId) === TYPE_MBA_STUDENT && <Impact baseline={filteredBaseline} final={final} />}
                        {Number(typeId) === TYPE_ACCELERATOR && <AcceleratorImpact baseline={filteredBaseline} final={final} />}
                    </TabPanel>
                </ContainerProvider>
            )}
            {isLoading && (
                <div className='loader-container'>
                    <Loader size={60} />
                </div>
            )}
        </div>
    );
};

export default E4ImpactMBA;
